/**
 * @component ry-progress-ring
 * @category  feedback
 * @summary   Circular SVG progress indicator. A two-circle ring (track + value arc) where the arc length reflects `value` ∈ [0, 100]. A centered slot lets you drop in a label, percentage, or icon. Mirrors Shoelace's `<sl-progress-ring>` API and matches the daisyUI `radial-progress` visual.
 *
 * Port reference:
 *   - Shoelace `<sl-progress-ring>` (MIT — Copyright (c) 2020 A Beautiful Site, LLC)
 *     https://github.com/shoelace-style/shoelace/blob/next/src/components/progress-ring/progress-ring.component.ts
 *   - daisyUI `radial-progress` (MIT — Pouya Saadeghi & contributors)
 *     https://daisyui.com/components/radial-progress/
 *
 * Sister component to <ry-progress> (linear). Pick the ring when the
 * footprint is small and circular (cards, KPI tiles, file-upload badges),
 * pick the bar when the value sits in a horizontal layout (forms, lists).
 *
 * @attribute value         number?   default=0     Current value, 0–100. Out-of-range values clamp visually but the attribute is preserved verbatim.
 * @attribute track-width   number?   default=4     Track (background ring) stroke thickness, in pixels.
 * @attribute value-width   number?   default=4     Value (progress arc) stroke thickness, in pixels. Mirrors Shoelace's `--indicator-width`; named `value-width` here to match the `value` attribute pairing.
 * @attribute label         string?                 Accessible label (sets `aria-label` on the host). Required for screen-reader context when the centered slot is empty or non-textual (icon).
 *
 * @property  value         number    Live numeric value clamped to [0, 100] at read time. Setting writes the `value` attribute.
 * @property  trackWidth    number    Live track-width.
 * @property  valueWidth    number    Live value-width.
 * @property  label         string    Live label (also reflected as `aria-label`).
 *
 * @event ry:change
 *        detail: { value: number }
 *        fires:  on every `value` attribute change (whether via setAttribute or the `.value` setter).
 *
 * @content   Default — a centered region rendered above the SVG. Drop in `%`, an `<ry-icon>`, or any inline content. Empty by default.
 *
 * @dom
 *   ry-progress-ring[value][track-width?][value-width?][label?]   role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow=value aria-label=label
 *     > svg.ry-progress-ring__image  [data-ry-target="image"]
 *         > circle.ry-progress-ring__track       [data-ry-target="track"]
 *         > circle.ry-progress-ring__indicator   [data-ry-target="indicator"]   style="stroke-dasharray:{c};stroke-dashoffset:{c - (value/100)*c}"
 *     > div.ry-progress-ring__content  [data-ry-target="content"]
 *         > {slotted children}
 *
 * @gotcha  Size is driven by `--ry-progress-ring-size` (default `8rem`), NOT an attribute. Use inline `style="--ry-progress-ring-size:160px"` or a CSS rule. Percentage sizes do not work (the SVG needs an absolute diameter to compute the arc radius).
 *
 * @gotcha  `value > 100` clamps visually to 100% but the attribute keeps the raw text. `el.value` returns the clamped number (max 100). Negative values clamp to 0.
 *
 * @gotcha  The arc starts at 12 o'clock and sweeps clockwise — the SVG is rotated -90deg so geometric 0° (3 o'clock) lands at the top. If you write your own theme override, preserve that rotation.
 *
 * @gotcha  `track-width` and `value-width` are pixel numbers. The SVG radius is computed as `(size/2) - max(track-width, value-width)/2` so the stroke never bleeds outside the host box. Asymmetric widths still draw centred-on-radius — pick small variations for a "halo" look.
 *
 * @example basic
 * ```html
 * <ry-progress-ring value="25"></ry-progress-ring>
 * ```
 *
 * @example with-percentage-label
 * ```html
 * <ry-progress-ring value="73" label="Sync progress">73%</ry-progress-ring>
 * ```
 *
 * @example custom-track-width
 * ```html
 * <ry-progress-ring value="60" track-width="10" value-width="10">60%</ry-progress-ring>
 * ```
 *
 * @example asymmetric-widths
 * ```html
 * <!-- Hairline track behind a thicker value arc -->
 * <ry-progress-ring value="40" track-width="2" value-width="10">40%</ry-progress-ring>
 * ```
 *
 * @example sized-via-css
 * ```html
 * <ry-progress-ring value="50" style="--ry-progress-ring-size: 12rem;">50%</ry-progress-ring>
 * ```
 *
 * @example with-icon
 * ```html
 * <ry-progress-ring value="100" label="Download complete">
 *   <ry-icon name="check"></ry-icon>
 * </ry-progress-ring>
 * ```
 *
 * @tutorial value-drives-stroke-dashoffset
 * ```js
 * // Wrong assumption: the value attribute alone drives the visual arc.
 * // Internally the value is converted to a dashoffset on the indicator
 * // circle. The dashoffset shrinks from circumference (0%) to 0 (100%).
 * const el = mount('<ry-progress-ring value="0"></ry-progress-ring>');
 * const indicator = el.querySelector('[data-ry-target="indicator"]');
 * const dasharray = parseFloat(indicator.style.strokeDasharray);
 * await expectEqual(parseFloat(indicator.style.strokeDashoffset), dasharray, '0% → offset equals full circumference');
 * el.value = 100;
 * await expectEqual(parseFloat(indicator.style.strokeDashoffset), 0, '100% → offset is zero');
 * el.value = 50;
 * await expectEqual(Math.round(parseFloat(indicator.style.strokeDashoffset)), Math.round(dasharray / 2), '50% → offset is half circumference');
 * ```
 *
 * @tutorial out-of-range-clamps
 * ```js
 * const el = mount('<ry-progress-ring value="150"></ry-progress-ring>');
 * await expectEqual(el.value, 100, 'clamped to 100');
 * await expectEqual(el.getAttribute('value'), '150', 'raw attribute preserved');
 * const negative = mount('<ry-progress-ring value="-20"></ry-progress-ring>');
 * await expectEqual(negative.value, 0, 'negative clamps to 0');
 * ```
 *
 * @tutorial value-attribute-fires-ry-change
 * ```js
 * const el = mount('<ry-progress-ring value="0"></ry-progress-ring>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail));
 * el.value = 42;
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 42);
 * ```
 *
 * @tutorial aria-attributes-mirror-state
 * ```js
 * const el = mount('<ry-progress-ring value="30" label="Sync"></ry-progress-ring>');
 * await expectAttr(el, 'role', 'progressbar');
 * await expectAttr(el, 'aria-valuemin', '0');
 * await expectAttr(el, 'aria-valuemax', '100');
 * await expectAttr(el, 'aria-valuenow', '30');
 * await expectAttr(el, 'aria-label', 'Sync');
 * el.value = 80;
 * await expectAttr(el, 'aria-valuenow', '80', 'aria-valuenow tracks value');
 * ```
 *
 * @tutorial slot-renders-in-content-area
 * ```js
 * // Centered text lives in [data-ry-target="content"], above the SVG.
 * const el = mount('<ry-progress-ring value="50">50%</ry-progress-ring>');
 * const content = el.querySelector('[data-ry-target="content"]');
 * await expectEqual(content?.textContent?.trim(), '50%');
 * ```
 *
 * @tutorial track-width-attribute-updates-stroke
 * ```js
 * const el = mount('<ry-progress-ring value="50" track-width="2"></ry-progress-ring>');
 * const track = el.querySelector('[data-ry-target="track"]');
 * await expectAttr(track, 'stroke-width', '2');
 * el.setAttribute('track-width', '12');
 * await expectAttr(track, 'stroke-width', '12');
 * ```
 *
 * @see ry-progress
 * @see ry-loader
 */

import { RyElement } from '../core/ry-element.js';

interface ProgressRingChangeDetail {
  value: number;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const DEFAULT_TRACK_WIDTH = 4;
const DEFAULT_VALUE_WIDTH = 4;

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

export class RyProgressRing extends RyElement {
  static get observedAttributes(): string[] {
    return ['value', 'track-width', 'value-width', 'label'];
  }

  #image: SVGSVGElement | null = null;
  #track: SVGCircleElement | null = null;
  #indicator: SVGCircleElement | null = null;

  setup(): void {
    this.#render();
    this.#applyAria();
    this.#applyGeometry();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;

    if (name === 'label') {
      this.#applyAria();
      return;
    }

    if (name === 'track-width' || name === 'value-width') {
      this.#applyGeometry();
      return;
    }

    if (name === 'value') {
      this.#applyAria();
      this.#applyGeometry();
      this.emit<ProgressRingChangeDetail>('change', { value: this.value });
    }
  }

  // ── Public API ──────────────────────────────────────────────────

  get value(): number {
    const raw = parseFloat(this.getAttribute('value') ?? '0');
    return clamp(Number.isFinite(raw) ? raw : 0, 0, 100);
  }

  set value(v: number) {
    this.setAttribute('value', String(v));
  }

  get trackWidth(): number {
    const raw = parseFloat(this.getAttribute('track-width') ?? String(DEFAULT_TRACK_WIDTH));
    return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_TRACK_WIDTH;
  }

  set trackWidth(v: number) {
    this.setAttribute('track-width', String(v));
  }

  get valueWidth(): number {
    const raw = parseFloat(this.getAttribute('value-width') ?? String(DEFAULT_VALUE_WIDTH));
    return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_VALUE_WIDTH;
  }

  set valueWidth(v: number) {
    this.setAttribute('value-width', String(v));
  }

  get label(): string {
    return this.getAttribute('label') ?? '';
  }

  set label(v: string) {
    if (v) this.setAttribute('label', v);
    else this.removeAttribute('label');
  }

  // ── Internals ───────────────────────────────────────────────────

  #render(): void {
    if (this.$('[data-ry-target="image"]')) return;

    // Preserve any user-slotted children — we move them into the content
    // wrapper after we build the SVG markup.
    const slotted: Node[] = [];
    while (this.firstChild) {
      slotted.push(this.firstChild);
      this.removeChild(this.firstChild);
    }

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('data-ry-target', 'image');
    svg.setAttribute('class', 'ry-progress-ring__image');
    svg.setAttribute('part', 'base');

    const track = document.createElementNS(SVG_NS, 'circle');
    track.setAttribute('data-ry-target', 'track');
    track.setAttribute('class', 'ry-progress-ring__track');

    const indicator = document.createElementNS(SVG_NS, 'circle');
    indicator.setAttribute('data-ry-target', 'indicator');
    indicator.setAttribute('class', 'ry-progress-ring__indicator');

    svg.appendChild(track);
    svg.appendChild(indicator);
    this.appendChild(svg);

    const content = document.createElement('div');
    content.setAttribute('data-ry-target', 'content');
    content.className = 'ry-progress-ring__content';
    for (const node of slotted) content.appendChild(node);
    this.appendChild(content);

    this.#image = svg;
    this.#track = track;
    this.#indicator = indicator;
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    this.setAttribute('aria-valuemax', '100');
    this.setAttribute('aria-valuenow', String(this.value));
    const label = this.getAttribute('label');
    if (label) {
      this.setAttribute('aria-label', label);
    } else {
      this.removeAttribute('aria-label');
    }
  }

  /**
   * Lay out the SVG and compute the dashoffset for the current value.
   * We resolve the diameter from --ry-progress-ring-size (defaulting to 128
   * if the var isn't readable, e.g. happy-dom without our CSS loaded). This
   * lets the math run consistently in tests without computed styles.
   */
  #applyGeometry(): void {
    if (!this.#image || !this.#track || !this.#indicator) return;

    const size = this.#resolveSize();
    const tw = this.trackWidth;
    const vw = this.valueWidth;
    const radius = size / 2 - Math.max(tw, vw) / 2;
    const cxcy = size / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (this.value / 100) * circumference;

    this.#image.setAttribute('width', String(size));
    this.#image.setAttribute('height', String(size));
    this.#image.setAttribute('viewBox', `0 0 ${size} ${size}`);

    for (const circle of [this.#track, this.#indicator] as SVGCircleElement[]) {
      circle.setAttribute('cx', String(cxcy));
      circle.setAttribute('cy', String(cxcy));
      circle.setAttribute('r', String(radius));
      circle.setAttribute('fill', 'none');
    }

    this.#track.setAttribute('stroke-width', String(tw));
    this.#indicator.setAttribute('stroke-width', String(vw));
    this.#indicator.style.strokeDasharray = String(circumference);
    this.#indicator.style.strokeDashoffset = String(offset);
    this.style.setProperty('--ry-progress-ring-percentage', String(this.value / 100));
  }

  #resolveSize(): number {
    // Prefer a computed --ry-progress-ring-size; fall back to 128px (the
    // Shoelace default). happy-dom may return an empty string; we treat that
    // as "use the default".
    let raw = '';
    try {
      raw = getComputedStyle(this).getPropertyValue('--ry-progress-ring-size').trim();
    } catch {
      raw = '';
    }
    if (raw) {
      const px = this.#cssLengthToPx(raw);
      if (px > 0) return px;
    }
    return 128;
  }

  #cssLengthToPx(value: string): number {
    if (value.endsWith('px')) {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    }
    if (value.endsWith('rem')) {
      const rootFontSize = (() => {
        try {
          return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        } catch {
          return 16;
        }
      })();
      const n = parseFloat(value);
      return Number.isFinite(n) ? n * rootFontSize : 0;
    }
    if (value.endsWith('em')) {
      const hostFontSize = (() => {
        try {
          return parseFloat(getComputedStyle(this).fontSize) || 16;
        } catch {
          return 16;
        }
      })();
      const n = parseFloat(value);
      return Number.isFinite(n) ? n * hostFontSize : 0;
    }
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ry-progress-ring': RyProgressRing;
  }
}

customElements.define('ry-progress-ring', RyProgressRing);
